import React, { ChangeEvent, useState } from 'react';
import type { HeadlineSegment } from '../../types';

// ─── Types ────────────────────────────────────────────────────

export interface TextFields {
  headline: string;
  subheadline: string;
  breakingNewsText: string;
  tickerText: string;
  reporterName: string;
  location: string;
  date: string;
}

export const EMPTY_TEXT_FIELDS: TextFields = {
  headline: '',
  subheadline: '',
  breakingNewsText: '',
  tickerText: '',
  reporterName: '',
  location: '',
  date: new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }),
};

export interface SegmentsProps {
  headlineSegments?: HeadlineSegment[];
  subheadlineSegments?: HeadlineSegment[];
  onAddHeadlineSegment?: () => void;
  onChangeHeadlineSegment?: (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => void;
  onRemoveHeadlineSegment?: (id: string) => void;
  onAddSubheadlineSegment?: () => void;
  onChangeSubheadlineSegment?: (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => void;
  onRemoveSubheadlineSegment?: (id: string) => void;
}

interface TextInputsProps extends SegmentsProps {
  values: TextFields;
  onChange: (field: keyof TextFields, value: string) => void;
}

// ─── Component ────────────────────────────────────────────────

export function TextInputs({
  values,
  onChange,
  headlineSegments,
  subheadlineSegments,
  onAddHeadlineSegment,
  onChangeHeadlineSegment,
  onRemoveHeadlineSegment,
  onAddSubheadlineSegment,
  onChangeSubheadlineSegment,
  onRemoveSubheadlineSegment,
}: TextInputsProps) {
  const handleChange =
    (field: keyof TextFields) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(field, e.target.value);
    };

  const hasHeadlineSegments = headlineSegments && headlineSegments.length > 0;
  const hasSubheadlineSegments = subheadlineSegments && subheadlineSegments.length > 0;

  return (
    <div className="space-y-3">
      {/* Headline field or segments editor */}
      {hasHeadlineSegments ? (
        <HeadlineSegmentsEditor
          label="Headline Segments"
          segments={headlineSegments}
          onAdd={onAddHeadlineSegment!}
          onChange={onChangeHeadlineSegment!}
          onRemove={onRemoveHeadlineSegment!}
        />
      ) : (
        <Field label="Headline" required>
          <textarea
            value={values.headline}
            onChange={handleChange('headline')}
            rows={2}
            maxLength={200}
            placeholder="Enter news headline…"
            className={textareaClass}
          />
          <CharCount value={values.headline} max={200} />
          {onAddHeadlineSegment && (
            <button
              type="button"
              onClick={onAddHeadlineSegment}
              className="mt-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
            >
              + Add timeline segments (multiple headlines at different times)
            </button>
          )}
        </Field>
      )}

      {/* Subheadline field or segments editor */}
      {hasSubheadlineSegments ? (
        <HeadlineSegmentsEditor
          label="Subheadline Segments"
          segments={subheadlineSegments}
          onAdd={onAddSubheadlineSegment!}
          onChange={onChangeSubheadlineSegment!}
          onRemove={onRemoveSubheadlineSegment!}
        />
      ) : (
        <Field label="Subheadline">
          <textarea
            value={values.subheadline}
            onChange={handleChange('subheadline')}
            rows={2}
            maxLength={300}
            placeholder="Enter subheadline…"
            className={textareaClass}
          />
          <CharCount value={values.subheadline} max={300} />
          {onAddSubheadlineSegment && (
            <button
              type="button"
              onClick={onAddSubheadlineSegment}
              className="mt-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
            >
              + Add timeline segments
            </button>
          )}
        </Field>
      )}

      <Field label="Breaking News Text">
        <input
          type="text"
          value={values.breakingNewsText}
          onChange={handleChange('breakingNewsText')}
          maxLength={120}
          placeholder="e.g. Markets tumble amid uncertainty"
          className={inputClass}
        />
      </Field>

      <Field label="Running Ticker">
        <textarea
          value={values.tickerText}
          onChange={handleChange('tickerText')}
          rows={2}
          maxLength={500}
          placeholder="Ticker items separated by ● (the template separator will be used automatically)"
          className={textareaClass}
        />
        <CharCount value={values.tickerText} max={500} />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Reporter Name">
          <input
            type="text"
            value={values.reporterName}
            onChange={handleChange('reporterName')}
            maxLength={60}
            placeholder="Jane Smith"
            className={inputClass}
          />
        </Field>

        <Field label="Location">
          <input
            type="text"
            value={values.location}
            onChange={handleChange('location')}
            maxLength={60}
            placeholder="New Delhi"
            className={inputClass}
          />
        </Field>
      </div>

      <Field label="Date">
        <input
          type="text"
          value={values.date}
          onChange={handleChange('date')}
          maxLength={40}
          placeholder="2 August 2026"
          className={inputClass}
        />
      </Field>
    </div>
  );
}

// ─── Headline Segments Editor ─────────────────────────────────

interface HeadlineSegmentsEditorProps {
  label: string;
  segments: HeadlineSegment[];
  onAdd: () => void;
  onChange: (id: string, field: keyof Omit<HeadlineSegment, 'id'>, value: string | number) => void;
  onRemove: (id: string) => void;
}

function HeadlineSegmentsEditor({ label, segments, onAdd, onChange, onRemove }: HeadlineSegmentsEditorProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div style={{ border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 10px', background: '#1e293b',
      }}>
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}
        >
          {collapsed ? '▶' : '▼'} {label} ({segments.length})
        </button>
        <button
          type="button"
          onClick={onAdd}
          style={{ background: '#1d4ed8', border: 'none', borderRadius: 4, color: '#fff', fontSize: 10, padding: '2px 8px', cursor: 'pointer', fontWeight: 600 }}
        >
          + Add
        </button>
      </div>

      {!collapsed && (
        <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {segments.length === 0 ? (
            <p style={{ fontSize: 10, color: '#475569', textAlign: 'center', padding: '8px 0' }}>
              No segments yet. Click "+ Add" to create one.
            </p>
          ) : (
            segments.map((seg, i) => (
              <div key={seg.id} style={{ border: '1px solid #334155', borderRadius: 5, padding: '6px 8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Segment {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => onRemove(seg.id)}
                    style={{ background: 'none', border: 'none', color: '#f87171', fontSize: 12, cursor: 'pointer', padding: '0 2px' }}
                  >
                    ✕
                  </button>
                </div>
                <textarea
                  value={seg.text}
                  onChange={(e) => onChange(seg.id, 'text', e.target.value)}
                  rows={2}
                  maxLength={200}
                  placeholder="Headline text for this segment…"
                  className={textareaClass}
                  style={{ marginBottom: 6 }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>
                    <label style={{ fontSize: 9, color: '#64748b', display: 'block', marginBottom: 2 }}>Start (sec)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.5}
                      value={seg.startSec}
                      onChange={(e) => onChange(seg.id, 'startSec', parseFloat(e.target.value) || 0)}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 9, color: '#64748b', display: 'block', marginBottom: 2 }}>End (sec, -1=end)</label>
                    <input
                      type="number"
                      min={-1}
                      step={0.5}
                      value={seg.endSec}
                      onChange={(e) => onChange(seg.id, 'endSec', parseFloat(e.target.value) || -1)}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-zinc-300">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function CharCount({ value, max }: { value: string; max: number }) {
  const len = value.length;
  const near = len > max * 0.85;
  return (
    <p className={`text-[10px] text-right mt-0.5 ${near ? 'text-yellow-400' : 'text-zinc-600'}`}>
      {len}/{max}
    </p>
  );
}

const inputClass =
  'w-full rounded-md bg-zinc-800 border border-zinc-600 px-2.5 py-1.5 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors';

const textareaClass =
  'w-full rounded-md bg-zinc-800 border border-zinc-600 px-2.5 py-1.5 text-xs text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors resize-none leading-relaxed';
